import AnimationController from '../../../assets/js/controllers/animation';
import CursorController from '../../../assets/js/controllers/cursor';
import ResizeController from '../../../assets/js/controllers/resize';

import { getRootOffsetBounds } from '../../../assets/js/helpers';

import CursorEvent from '../cursor-event';
import ModuleType from '../../../assets/js/constants/module-type';
import Platform from '../../../assets/js/constants/platform';

export default class CursorCircleContext {

	static selector = 'cursor-circle-context';

	// class variables

	config = {
		type: ModuleType.SLIDER_IMAGE,
		slider: {
			boundaries: {
				top: 100,
				bottom: 100,
				left: 250,
				right: 250
			}
		},
		delays: {
			drag: 160,
			show: 600,
			hide: 600
		}
	};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		lerpCursor: {
			x: 0,
			y: 0
		},
		slider: {
			boundaries: {
				top: 0,
				bottom: 0,
				left: 0,
				right: 0
			}
		},
		wrapper: null
	};

	el = {};

	flags = {
		// status
		isEnabled: true,
		isShowing: false,
		isShowComplete: false,
		isHideComplete: true,
		willShow: false,
		willHide: false,

		// mouse position
		isMouseOver: false,

		// lerped cursor position
		isLerpOver: false,

		// slider
		isSlider: false,
		isSliderDragging: false,

		// video
		isVideo: false,
		isVideoPlaying: false,

		// changing cursor to left / right arrows
		isEdgeLeft: false,
		isEdgeLeftEnabled: true,
		isEdgeRight: false,
		isEdgeRightEnabled: true
	};

	states = {
		platform: ''
	};

	timeouts = {
		mouseDown: null,
		transition: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			controls: {
				videoPause: container.querySelector(`.${this.constructor.selector}__video-pause`),
				videoPlay: container.querySelector(`.${this.constructor.selector}__video-play`)
			},
			wrapper: container.parentElement
		};

		this.initialize();
	}

	// initialize

	initialize() {
		if (this.config.type === ModuleType.SLIDER_IMAGE || this.config.type === ModuleType.SLIDER_VIDEO) {
			this.flags.isSlider = true;
		} else {
			this.flags.isSlider = false;
		}

		if (this.config.type === ModuleType.SLIDER_VIDEO || this.config.type === ModuleType.VIDEO) {
			this.flags.isVideo = true;
		} else {
			this.flags.isVideo = false;
		}

		this.#addEventListeners();
	}

	// event listeners

	#addEventListeners = () => {
		this.el.container.addEventListener(CursorEvent.HIDE, this.#onTriggerHide);
		this.el.container.addEventListener(CursorEvent.DISABLE_LEFT, this.#onTriggerDisableLeft);
		this.el.container.addEventListener(CursorEvent.DISABLE_RIGHT, this.#onTriggerDisableRight);
		this.el.container.addEventListener(CursorEvent.ENABLE_LEFT, this.#onTriggerEnableLeft);
		this.el.container.addEventListener(CursorEvent.ENABLE_RIGHT, this.#onTriggerEnableRight);
		this.el.container.addEventListener(CursorEvent.SHOW, this.#onTriggerShow);
		this.el.container.addEventListener(CursorEvent.VIDEO_PAUSE, this.#onTriggerVideoPause);
		this.el.container.addEventListener(CursorEvent.VIDEO_PLAY, this.#onTriggerVideoPlay);

		this.el.wrapper.addEventListener('mousedown', this.#onMouseDown);
		this.el.wrapper.addEventListener('mouseup', this.#onMouseUp);

		document.addEventListener('mouseleave', this.#onDocumentMouseLeave);

		this.controllers.animation = AnimationController.set({
			update: this.#onAnimationUpdate,
			render: this.#onAnimationRender
		});

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.measure();
	}

	#removeEventListeners = () => {
		this.el.container.removeEventListener(CursorEvent.HIDE, this.#onTriggerHide);
		this.el.container.removeEventListener(CursorEvent.DISABLE_LEFT, this.#onTriggerDisableLeft);
		this.el.container.removeEventListener(CursorEvent.DISABLE_RIGHT, this.#onTriggerDisableRight);
		this.el.container.removeEventListener(CursorEvent.ENABLE_LEFT, this.#onTriggerEnableLeft);
		this.el.container.removeEventListener(CursorEvent.ENABLE_RIGHT, this.#onTriggerEnableRight);
		this.el.container.removeEventListener(CursorEvent.SHOW, this.#onTriggerShow);
		this.el.container.removeEventListener(CursorEvent.VIDEO_PAUSE, this.#onTriggerVideoPause);
		this.el.container.removeEventListener(CursorEvent.VIDEO_PLAY, this.#onTriggerVideoPlay);

		this.el.wrapper.removeEventListener('mousedown', this.#onMouseDown);
		this.el.wrapper.removeEventListener('mouseup', this.#onMouseUp);

		document.removeEventListener('mouseleave', this.#onDocumentMouseLeave);

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);

		this.controllers.animation = null;
		this.controllers.resize = null;
	}

	// listener methods

	#onTriggerHide = () => {
		this.statusLockHide();
	}

	#onTriggerDisableLeft = () => {
		this.flags.isEdgeLeftEnabled = false;
	}

	#onTriggerDisableRight = () => {
		this.flags.isEdgeRightEnabled = false;
	}

	#onTriggerEnableLeft = () => {
		this.flags.isEdgeLeftEnabled = true;
	}

	#onTriggerEnableRight = () => {
		this.flags.isEdgeRightEnabled = true;
	}

	#onTriggerShow = () => {
		this.statusLockShow();
	}

	#onTriggerVideoPause = () => {
		this.flags.isVideoPlaying = false;
	}

	#onTriggerVideoPlay = () => {
		this.flags.isVideoPlaying = true;
	}

	#onMouseDown = () => {
		this.checkMouseDown();
	}

	#onMouseUp = () => {
		this.checkMouseUp();
	}

	#onDocumentMouseLeave = () => {
		this.checkMouseUp();
	}

	// controller methods

	#onAnimationUpdate = () => this.measureCursor();
	#onAnimationRender = () => this.renderCursor();

	#onResizeUpdate = () => this.measureResize();
	#onResizeRender = () => this.renderResize();

	// measure all (via update)

	measure() {
		this.measureResize();
		this.measureCursor();
	}

	// cursor

	measureCursor() {
		let isRendering = false;

		// calculate if user cursor is within parent wrapper boundaries

		const inX = (CursorController.cursorX >= this.coords.wrapper.left && CursorController.cursorX <= this.coords.wrapper.right);
		const inY = (CursorController.cursorY >= this.coords.wrapper.top && CursorController.cursorY <= this.coords.wrapper.bottom);

		const lerpInX = (CursorController.lerpX >= this.coords.wrapper.left && CursorController.lerpX <= this.coords.wrapper.right);
		const lerpInY = (CursorController.lerpY >= this.coords.slider.boundaries.top && CursorController.lerpY <= this.coords.slider.boundaries.bottom);

		this.flags.isLerpOver = (lerpInX && lerpInY);
		this.flags.isMouseOver = (inX && inY);

		// if status is not locked, detect whether cursor should be shown or hidden
		// status would be locked by external controls forcing the cursor to show or hide

		if (!this.flags.isStatusLock) {
			if (this.flags.isLerpOver && this.flags.isMouseOver) {
				if (!this.flags.willShow && !this.flags.isShowing) {
					this.flags.willShow = true;
					isRendering = true;
				}
			} else {
				if (!this.flags.willHide && (this.flags.willShow || this.flags.isShowing)) {
					this.flags.willShow = false;
					this.flags.willHide = true;
					isRendering = true;
				}
			}
		}

		if ((this.flags.isShowing || !this.flags.isHideComplete) && this.states.platform !== Platform.MOBILE) {
			this.coords.lerpCursor.x = CursorController.lerpX - this.coords.wrapper.left;
			this.coords.lerpCursor.y = CursorController.lerpY - this.coords.wrapper.top;
			isRendering = true;
		}

		// if slider, calculate whether to change cursor state based on position

		if (this.flags.isSlider && this.states.platform === Platform.DESKTOP) {
			const isEdgeLeft = this.flags.isEdgeLeftEnabled && (CursorController.cursorX <= this.coords.slider.boundaries.left);
			const isEdgeRight = this.flags.isEdgeRightEnabled && (CursorController.cursorX >= this.coords.slider.boundaries.right);

			if (isEdgeLeft && this.flags.isEdgeLeft) { isRendering = true; }
			if (isEdgeRight && this.flags.isEdgeRight) { isRendering = true; }

			this.flags.isEdgeLeft = isEdgeLeft;
			this.flags.isEdgeRight = isEdgeRight;
		}

		return isRendering;
	}

	renderCursor() {
		if (!this.flags.isSliderDragging && this.flags.isEdgeLeft && this.flags.isEdgeLeftEnabled) {
			this.el.container.classList.add('is-edge-left');
		} else {
			this.el.container.classList.remove('is-edge-left');
		}

		if (!this.flags.isSliderDragging && this.flags.isEdgeRight && this.flags.isEdgeRightEnabled) {
			this.el.container.classList.add('is-edge-right');
		} else {
			this.el.container.classList.remove('is-edge-right');
		}

		if (this.flags.willShow) {
			this.show();
		} else if (this.flags.willHide) {
			this.hide();
		}

		this.el.container.style.transform = `translateX(${this.coords.lerpCursor.x}px) translateY(${this.coords.lerpCursor.y}px)`;
	}

	// resize

	measureResize() {
		this.states.platform = ResizeController.platform;
		this.coords.wrapper = getRootOffsetBounds(this.el.wrapper);

		// store position calculations for animation use

		if (this.flags.isSlider) {
			this.coords.slider.boundaries.left = this.coords.wrapper.left + this.config.slider.boundaries.left;
			this.coords.slider.boundaries.right = this.coords.wrapper.right - this.config.slider.boundaries.right;
		}

		this.coords.slider.boundaries.top = this.coords.wrapper.top + this.config.slider.boundaries.top;
		this.coords.slider.boundaries.bottom = this.coords.wrapper.bottom - this.config.slider.boundaries.bottom;

		return ResizeController.isPlatformChange();
	}

	renderResize() {
		if (this.states.platform === Platform.MOBILE) {
			this.hide();
		} else {
			this.show();
		}
	}

	// determine cursor drag

	checkMouseDown() {
		if (this.flags.isShowing) {
			if (this.flags.isEdgeLeft || this.flags.isEdgeRight) {
				if (!this.timeouts.mouseDown) {
					this.timeouts.mouseDown = setTimeout(() => {
						this.showDragging();
					}, this.config.delays.drag);
				}
			} else {
				this.showDragging();
			}
		}
	}

	checkMouseUp() {
		this.clearDraggingTimeout();
		this.flags.isSliderDragging = false;
		this.el.container.classList.remove('is-mouse-down');
	}


	showDragging() {
		this.clearDraggingTimeout();
		this.flags.isSliderDragging = true;
		this.el.container.classList.add('is-mouse-down');
	}

	clearDraggingTimeout() {
		clearTimeout(this.timeouts.mouseDown);
		this.timeouts.mouseDown = null;
	}

	// hide / show

	hide() {
		if (this.flags.isShowing) {
			this.flags.willHide = false;
			this.flags.willShow = false;
			this.flags.isShowing = false;
			this.flags.isShowComplete = false;
			this.el.container.classList.add('is-hidden');

			clearTimeout(this.timeouts.transition);
			this.timeouts.transition = setTimeout(() => {
				this.flags.isHideComplete = true;
			}, this.config.delays.hide);
		}
	}

	show() {
		if (!this.flags.isShowing) {
			this.flags.willHide = false;
			this.flags.willShow = false;
			this.flags.isShowing = true;
			this.flags.isHideComplete = false;
			this.el.container.classList.remove('is-hidden');

			clearTimeout(this.timeouts.transition);
			this.timeouts.transition = setTimeout(() => {
				this.flags.isShowComplete = true;
			}, this.config.delays.show);
		}
	}

	statusLockHide() {
		this.flags.isStatusLock = true;
		this.hide();
	}

	statusLockShow() {
		this.flags.isStatusLock = false;
		this.show();
	}

	// disable / enable

	disable() {
		if (this.flags.isEnabled) {
			this.flags.isEnabled = false;
			this.el.container.classList.remove('is-enabled');
		}
	}

	enable() {
		if (!this.flags.isEnabled) {
			this.flags.isEnabled = true;
			this.el.container.classList.add('is-enabled');
		}
	}

	//

	destroy() {
		this.#removeEventListeners();
	}

}
